import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { verify2FA } from './features/mfaSlice';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grow,
  TextField,
  Button,
} from '@mui/material';
import images from '../src/assests/images/bk_img1.jpg';
import logo from '../src/assests/images/logo1.png';
import { useIatfContext } from "./Usecontext";

const VerifyMFA = () => {
  const [mfaCode, setMfaCode] = useState('');
  const [showCard, setShowCard] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.mfa);
  const { loginDetails, setLoginDetails } = useIatfContext();

  useEffect(() => {
    setShowCard(true);
  }, []);

  const handleVerify = async () => {
    if (mfaCode.trim()) {
      // Get user email from localStorage
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const email = userData.email;

      if (email) {
        const payload = {
          email: email,
          token: mfaCode.trim(),
        };

        try {
          const resultAction = await dispatch(verify2FA(payload));
          if (verify2FA.fulfilled.match(resultAction)) {
            // Set MFA as enabled
            localStorage.setItem('mfaEnabled', 'true');
            localStorage.setItem('isLoggedIn', 'true');
            setLoginDetails({ email });
            // Verification successful, redirect to homepage with navbar
            navigate('/Homepage');
          }
        } catch (error) {
          console.error("Verification error:", error);
        }
      }
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleVerify();
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        height: "100vh",
        width: "100%",
        overflow: "hidden",
        // Background image styling
        backgroundImage: `url(${images})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Semi-transparent overlay to improve text readability */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.4)", // Dark overlay for better contrast
          zIndex: 0,
        }}
      />

      {/* Centered card with animation */}
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
        px={2}
        position="relative" // Ensure it's above the overlay
        zIndex={1}
      >
        <Grow in={showCard} timeout={1000}>
          <Card
            sx={(theme) => ({
              width: {
                xs: "90%",
                sm: "65%",
                md: "45%",
                lg: "35%",
                xl: "28%",
              },
              maxWidth: 450,
              padding: 2,
              backgroundColor: "rgba(255, 255, 255, 0.95)", // Slightly transparent white
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              boxShadow: 3,
              borderRadius: 2,
              backdropFilter: "blur(5px)", // Frosted glass effect
            })}
          >
            <CardContent
              sx={{ width: "100%", p: 1, "&:last-child": { pb: 1 } }}
            >
              {/* Logo with description below */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mb: 1.5,
                }}
              >
                <img
                  src={logo}
                  alt="Lucas TVs Logo"
                  style={{ width: "100%", maxWidth: "150px", height: "auto" }}
                />
                <Typography
                  variant="subtitle1"
                  align="center"
                  color="text.primary"
                  fontSize="16px"
                  fontWeight="Bold"
                  sx={{ marginTop: 0.5 }}
                >
                  MFA
                </Typography>
              </Box>

              {/* Verify MFA Message */}
              <Typography
                variant="h6"
                align="center"
                sx={{ mb: 2, color: "text.primary" }}
              >
                Verify Your MFA Code
              </Typography>

              <Typography
                variant="body1"
                align="center"
                sx={{ mb: 3, color: "text.secondary" }}
              >
                Enter the 6-digit code from your authenticator app.
              </Typography>

              {/* MFA Code Input */}
              <TextField
                label="MFA Code"
                variant="outlined"
                fullWidth
                margin="dense"
                size="small"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                onKeyPress={handleKeyPress}
                sx={{ mb: 2 }}
              />

              {/* Verify Button */}
              <Button
                variant="contained"
                size="small"
                fullWidth
                sx={{
                  paddingX: 3,
                  fontSize: "0.9rem",
                  backgroundImage: "linear-gradient(45deg, #7DF9FF, #00BFFF)",
                  "&:hover": {
                    backgroundImage: "linear-gradient(45deg, #00BFFF, #7DF9FF)",
                  },
                  height: "40px",
                  marginTop: "8px",
                }}
                onClick={handleVerify}
                disabled={loading}
              >
                {loading ? "Verifying..." : "Verify MFA"}
              </Button>

              {/* Error message */}
              {error && (
                <Typography
                  variant="body2"
                  align="center"
                  sx={{ color: "error.main", mt: 2 }}
                >
                  {error.message || 'Verification failed'}
                </Typography>
              )}

              {/* Back Button */}
              <Button
                variant="outlined"
                size="small"
                fullWidth
                sx={{
                  paddingX: 3,
                  fontSize: "0.9rem",
                  height: "40px",
                  marginTop: "16px",
                  borderColor: "#00BFFF",
                  color: "#00BFFF",
                  "&:hover": {
                    borderColor: "#7DF9FF",
                    color: "#7DF9FF",
                  },
                }}
                onClick={() => navigate('/welcome')}
              >
                Back
              </Button>
            </CardContent>
          </Card>
        </Grow>
      </Box>
    </Box>
  );
};

export default VerifyMFA;